/**
 * src/services/projects.api.ts 의 짝.
 *
 * 프로젝트 본체는 뼈대만 담고, 용도·면적·공사비 같은 값은 상세 항목(detailFields·projectDetails)에
 * 있다. 금액 키는 **권한이 없으면 응답에서 빼고**, 금액 전용 경로는 403 으로 막는다 — 화면에서
 * 가리는 것이 아니다.
 *
 * 단계 상태(진행·임박·지연 …)와 계약 금액, 수익성은 모두 계산값이다. 저장하지 않는다.
 */
import { HttpResponse, http } from "msw";
import { shiftDate, today } from "@/lib/dates";
import type {
  BillingInput,
  Contract,
  ContractInput,
  DetailValue,
  DetailValueInput,
  Income,
  Ledger,
  LedgerRow,
  MemberAssignInput,
  AllocationInput,
  MoneySummary,
  Outsourcing,
  OutsourcingInput,
  Phase,
  PhaseCreateInput,
  PhaseDatesInput,
  PhaseLogInput,
  PhaseUpdateInput,
  PhaseStatus,
  ProcessRow,
  Project,
  ProjectCreateInput,
  ProjectDetail,
  ProjectMember,
  ProjectMoney,
  ProjectPatch,
  RevisionInput,
  UserRef,
} from "@/types";
import type { MockPhase, MockProject } from "../data/projects";
import { db } from "../db";
import { fail, latency, requireAdmin, requireUser } from "../http";

const BASE = "/api/v1";
/** 마감이 이 날 수 안이면 임박이다. */
const DUE_SOON_DAYS = 7;
const KIND_LABEL = { outsource: "외주", extra_service: "추가 용역", direct: "직접 경비" } as const;
const STATUS_LABEL = {
  planned: "착수예정",
  active: "진행중",
  hold: "보류",
  done: "완료",
  cancelled: "중단",
} as const;
let seq = 0;

/** 재무는 관리자만 본다. */
const canSeeMoney = (userId: string) =>
  db.users.find((user) => user.id === userId)?.role === "admin";

const days = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

/** 단계 상태 — 날짜에서 계산한다. backend `app/projects/phases.py` 와 같은 규칙이다. */
export function phaseState(
  phase: MockPhase,
  now: string
): { status: PhaseStatus; days_left: number | null } {
  if (phase.done_date) return { status: "done", days_left: null };
  const left = phase.due_date ? days(now, phase.due_date) : null;
  if (phase.due_date && phase.due_date < now) return { status: "delayed", days_left: left };
  if (left !== null && left <= DUE_SOON_DAYS && phase.start_date) {
    return { status: "due_soon", days_left: left };
  }
  if (phase.start_date && phase.start_date <= now) return { status: "progress", days_left: left };
  return { status: "pending", days_left: left };
}

const toPhase = (phase: MockPhase, now: string): Phase => ({
  id: phase.id,
  name: phase.name,
  template_key: phase.template_key,
  ordinal: phase.ordinal,
  start_label: phase.start_label,
  end_label: phase.end_label,
  start_date: phase.start_date,
  due_date: phase.due_date,
  done_date: phase.done_date,
  ...phaseState(phase, now),
  logs: [...phase.logs].sort((a, b) => a.log_date.localeCompare(b.log_date)),
});

const phasesOf = (projectId: string, now: string) =>
  db.projectPhases
    .filter((phase) => phase.project_id === projectId)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((phase) => toPhase(phase, now));

const userRef = (userId: string): UserRef => ({
  id: userId,
  name: db.users.find((user) => user.id === userId)?.name ?? userId,
});

const membersOf = (projectId: string): ProjectMember[] =>
  db.projectMembers
    .filter((member) => member.project_id === projectId)
    .sort((a, b) => a.assigned_on.localeCompare(b.assigned_on))
    .map((member) => ({
      id: member.id,
      user: userRef(member.user_id),
      assigned_on: member.assigned_on,
      released_on: member.released_on,
      plans: db.projectMemberPlans
        .filter((plan) => plan.project_member_id === member.id)
        .sort((a, b) => a.period_from.localeCompare(b.period_from))
        .map(({ project_member_id: _, ...plan }) => plan),
    }));

/**
 * 쓸 수 있는 기간인지 — 서버와 같은 둘을 본다
 * (backend `app/projects/service.py` 의 `_assert_plan_in_member_period`).
 *
 * 겹침은 보지 않는다. 겹치는 계획은 거절하는 것이 아니라 **잘라내는 것**이기 때문이다.
 */
function allocationProblem(
  member: (typeof db.projectMembers)[number],
  input: AllocationInput
): Response | null {
  if (input.period_to < input.period_from) return fail(422, "종료일이 시작일보다 앞섭니다.");
  if (
    input.period_from < member.assigned_on ||
    (member.released_on && input.period_to > member.released_on)
  )
    return fail(422, "투입 계획 기간이 할당 기간을 벗어납니다.");
  return null;
}

/**
 * 하루 차이 — 자르고 붙일 때 경계를 한 칸 옮긴다.
 *
 * 직접 Date 를 만들어 `toISOString` 으로 자르면 안 된다. 로컬 자정(KST)이 UTC 로는 전날
 * 15시라, 돌아온 날짜가 하루 당겨진다 — 경계가 1일씩 어긋나 계획이 겹치거나 구멍이 난다.
 */
const shift = (date: string, days: number) => shiftDate(date, days);

/** 지금 배치된 사람 중 PM 표시가 있는 사람들 — PM 은 권한 없는 이름표다. */
const pmsOf = (projectId: string): UserRef[] =>
  db.projectMembers
    .filter(
      (member) =>
        member.project_id === projectId && member.released_on === null && member.is_pm
    )
    .map((member) => userRef(member.user_id));

/** 상세 항목 — 금액 종류는 권한이 없으면 칸째 빠진다. */
function detailsOf(projectId: string, money: boolean): DetailValue[] {
  return db.detailFields
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .flatMap((field) => {
      if (field.value_type === "money" && !money) return [];
      const value = db.projectDetails.find(
        (row) => row.project_id === projectId && row.field_id === field.id
      );
      if (!value) return [];
      return [
        {
          field_id: field.id,
          key: field.key,
          label: field.label,
          value_type: field.value_type,
          unit: field.unit,
          value_text: value.value_text,
          value_number: value.value_number,
          value_date: value.value_date,
        },
      ];
    });
}

const usageOf = (projectId: string) =>
  db.projectDetails.find((row) => row.project_id === projectId && row.field_id === "field-usage")
    ?.value_text ?? "";

const toProject = (project: MockProject): Project => ({
  id: project.id,
  code: project.code,
  name: project.name,
  usage: usageOf(project.id),
  color: project.color,
  status: project.status,
  status_label: STATUS_LABEL[project.status],
  // 지금 들어가 있는 사람만, 이름순 — 서버와 같다.
  members: [
    ...new Map(
      membersOf(project.id)
        .filter((member) => member.released_on === null)
        .map((member) => [member.user.id, member.user])
    ).values(),
  ].sort((a, b) => a.name.localeCompare(b.name, "ko")),
});

function toDetail(project: MockProject, money: boolean): ProjectDetail {
  const now = today();
  return {
    id: project.id,
    code: project.code,
    name: project.name,
    color: project.color,
    status: project.status,
    status_label: STATUS_LABEL[project.status],
    pms: pmsOf(project.id),
    started_on: project.started_on,
    ended_on: project.ended_on,
    budget_hours: project.budget_hours,
    memo: project.memo,
    folder_ready: project.nas_path !== "",
    created_at: `${project.started_on ?? "2026-01-01"}T09:00:00+09:00`,
    details: detailsOf(project.id, money),
    phases: phasesOf(project.id, now),
    members: membersOf(project.id),
  };
}

/**
 * 실적 — 제출한 Daily 의 그 프로젝트 줄 시간을 사람 · 날짜(또는 주)마다 더한다. 서버처럼
 * 날짜 순이고, 배치 밖의 사람도 들어간다(actual_users 로 이름을 준다).
 */
function actualsOf(projectId: string, step: "day" | "week") {
  const sums = new Map<string, { user_id: string; work_date: string; hours: number }>();
  for (const daily of db.dailies) {
    if (daily.status !== "submitted") continue;
    const on = step === "day" ? daily.work_date : daily.week_start;
    for (const row of daily.rows) {
      if (row.project_id !== projectId || !row.actual_hours) continue;
      const key = `${daily.user_id}|${on}`;
      const sum = sums.get(key) ?? { user_id: daily.user_id, work_date: on, hours: 0 };
      sum.hours += row.actual_hours;
      sums.set(key, sum);
    }
  }
  const actuals = [...sums.values()]
    .sort((a, b) => a.work_date.localeCompare(b.work_date))
    .map((sum) => ({ ...sum, hours: sum.hours.toFixed(2) }));
  const actual_users = [...new Set(actuals.map((actual) => actual.user_id))]
    .map(userRef)
    .sort((a, b) => a.name.localeCompare(b.name, "ko"));
  return { actuals, actual_users };
}

function toProcessRow(project: MockProject, now: string, step: "day" | "week"): ProcessRow {
  const phases = phasesOf(project.id, now);
  return {
    id: project.id,
    code: project.code,
    name: project.name,
    color: project.color,
    status: project.status,
    status_label: STATUS_LABEL[project.status],
    pms: pmsOf(project.id),
    is_delayed: phases.some((phase) => phase.status === "delayed"),
    phases,
    members: membersOf(project.id),
    ...actualsOf(project.id, step),
  };
}

/* ── 금액 ──────────────────────────────────────────────────────────────── */

/** 그 시점에 유효한 금액 — 개정 이력의 기간에서 뽑는다. 저장된 값이 아니다. */
const amountOn = (contract: { revisions: Contract["revisions"] }, now: string) =>
  contract.revisions.find(
    (revision) =>
      revision.voided_at === null &&
      revision.effective_from <= now &&
      (revision.effective_to === null || revision.effective_to > now)
  )?.amount ?? null;

const incomesOf = (billingId: string) =>
  db.incomes.filter((income) => income.billing_id === billingId);

function toContract(contract: (typeof db.contracts)[number], now: string): Contract {
  const billings = db.billings
    .filter((billing) => billing.contract_id === contract.id)
    .sort((a, b) => a.seq - b.seq)
    .map((billing) => {
      const received = incomesOf(billing.id).reduce((sum, income) => sum + income.amount, 0);
      return {
        id: billing.id,
        seq: billing.seq,
        title: billing.title,
        amount: billing.amount,
        phase_id: billing.phase_id,
        planned_on: billing.planned_on,
        billed_on: billing.billed_on,
        due_on: billing.due_on,
        memo: billing.memo,
        received,
        receivable: billing.amount - received,
        incomes: incomesOf(billing.id).map(
          ({ project_id: _project, contract_id: _contract, ...income }) => income
        ),
      };
    });
  return {
    id: contract.id,
    seq: contract.seq,
    title: contract.title,
    client_name: contract.client_name,
    signed_on: contract.signed_on,
    contract_from: contract.contract_from,
    contract_to: contract.contract_to,
    vat_included: contract.vat_included,
    memo: contract.memo,
    amount: amountOn(contract, now),
    revisions: contract.revisions,
    phase_fees: contract.phase_fees,
    billings,
  };
}

const toOutsourcing = (row: (typeof db.outsourcings)[number]): Outsourcing => ({
  id: row.id,
  seq: row.seq,
  kind: row.kind,
  kind_label: KIND_LABEL[row.kind],
  payee_name: row.payee_name,
  title: row.title,
  amount: row.amount,
  vat_included: row.vat_included,
  ordered_on: row.ordered_on,
  planned_on: row.planned_on,
  paid_on: row.paid_on,
  phase_id: row.phase_id,
  memo: row.memo,
});

/** 인건비 원가 — 제출한 Daily 시간 × 시간단가. mock 은 공통 단가 하나로 어림한다. */
const HOURLY_COST = 36_000;

function moneyOf(projectId: string): ProjectMoney {
  const now = today();
  const contracts = db.contracts
    .filter((contract) => contract.project_id === projectId)
    .sort((a, b) => a.seq - b.seq)
    .map((contract) => toContract(contract, now));
  const outsourcings = db.outsourcings
    .filter((row) => row.project_id === projectId)
    .sort((a, b) => a.seq - b.seq)
    .map(toOutsourcing);
  const billings = contracts.flatMap((contract) => contract.billings);
  const billed = billings
    .filter((billing) => billing.billed_on)
    .reduce((sum, billing) => sum + billing.amount, 0);
  const received = billings.reduce((sum, billing) => sum + billing.received, 0);
  const hours = db.dailies
    .filter((daily) => daily.status === "submitted")
    .flatMap((daily) => daily.rows.filter((row) => row.project_id === projectId))
    .reduce((sum, row) => sum + row.actual_hours, 0);
  const paid = outsourcings.filter((row) => row.paid_on).reduce((sum, row) => sum + row.amount, 0);
  const planned = outsourcings
    .filter((row) => !row.paid_on)
    .reduce((sum, row) => sum + row.amount, 0);
  const labor = Math.round(hours * HOURLY_COST);
  const contractAmount = contracts.reduce((sum, contract) => sum + (contract.amount ?? 0), 0);
  const summary: MoneySummary = {
    as_of: now,
    contract_amount: contractAmount,
    billed,
    received,
    receivable: billed - received,
    outsourcing_paid: paid,
    outsourcing_planned: planned,
    labor_cost: labor,
    profit: contractAmount - paid - labor,
    profit_with_planned: contractAmount - paid - planned - labor,
  };
  return { contracts, outsourcings, summary };
}

/** 금액을 고치는 경로의 공통 가드 — 권한과 프로젝트 존재를 함께 본다. */
function moneyGuard(projectId: string) {
  const user = requireUser();
  if (user instanceof Response) return user;
  if (!canSeeMoney(user.id)) return fail(403, "금액을 고칠 권한이 없습니다.");
  if (!db.projects.some((project) => project.id === projectId))
    return fail(404, "없는 프로젝트입니다.");
  return user;
}

export const projectHandlers = [
  http.get(`${BASE}/projects/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = db.projects
      .slice()
      .sort((a, b) => (b.code ?? "").localeCompare(a.code ?? ""))
      .map(toProject);
    return HttpResponse.json({ results });
  }),

  /** 고르는 칸 · 문서 값이 쓰는 가벼운 목록 — 상태와 상관없이 전부, 번호순. `:id` 보다 앞에 둔다. */
  http.get(`${BASE}/projects/refs/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = db.projects
      .slice()
      .sort((a, b) => (a.code ?? "~").localeCompare(b.code ?? "~"))
      .map((project) => ({
        id: project.id,
        code: project.code,
        name: project.name,
        color: project.color,
        status: project.status,
        status_label: STATUS_LABEL[project.status],
      }));
    return HttpResponse.json({ results });
  }),

  http.get(`${BASE}/projects/process/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const query = new URL(request.url).searchParams;
    const year = query.get("year");
    const status = query.get("status");
    const keyword = (query.get("keyword") ?? "").trim().toLowerCase();
    const projectId = query.get("project_id");
    const step = query.get("actual_step") === "day" ? "day" : "week";
    const limit = Number(query.get("limit") ?? Infinity);
    const offset = Number(query.get("offset") ?? 0);
    const now = today();
    const rows = db.projects
      .filter((project) => !projectId || project.id === projectId)
      .filter((project) => !status || status === "all" || project.status === status)
      .filter((project) => !year || (project.started_on ?? "").startsWith(year))
      .filter(
        (project) =>
          !keyword ||
          project.name.toLowerCase().includes(keyword) ||
          (project.code ?? "").includes(keyword)
      )
      .sort((a, b) => (a.code ?? "~").localeCompare(b.code ?? "~"));
    const results = rows
      .slice(offset, offset + limit)
      .map((project) => toProcessRow(project, now, step));
    return HttpResponse.json({ results, total: rows.length });
  }),

  http.get(`${BASE}/project-detail-fields/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = [...db.detailFields].sort((a, b) => a.sort_order - b.sort_order);
    return HttpResponse.json({ results });
  }),

  http.get(`${BASE}/projects/ledger/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const query = new URL(request.url).searchParams;
    const year = query.get("year");
    const usage = query.get("usage");
    const workScope = query.get("work_scope");
    const keyword = (query.get("keyword") ?? "").trim().toLowerCase();
    const money = canSeeMoney(user.id);
    const now = today();

    const detailText = (projectId: string, key: string) =>
      db.projectDetails.find(
        (row) => row.project_id === projectId && row.field_id === `field-${key}`
      )?.value_text ?? null;

    const rows: LedgerRow[] = db.projects
      .filter((project) => !year || (project.started_on ?? "").startsWith(year))
      .filter((project) => !usage || detailText(project.id, "usage") === usage)
      .filter((project) => !workScope || detailText(project.id, "work_scope") === workScope)
      .filter(
        (project) =>
          !keyword ||
          project.name.toLowerCase().includes(keyword) ||
          (project.code ?? "").includes(keyword)
      )
      .sort((a, b) => (b.code ?? "").localeCompare(a.code ?? ""))
      .map((project) => {
        const contracts = db.contracts.filter((contract) => contract.project_id === project.id);
        return {
          id: project.id,
          code: project.code,
          name: project.name,
          color: project.color,
          status: project.status,
          status_label: STATUS_LABEL[project.status],
          pms: pmsOf(project.id),
          members: membersOf(project.id).map((member) => member.user),
          client_names: [
            ...new Set(contracts.map((contract) => contract.client_name).filter(Boolean)),
          ],
          started_on: project.started_on,
          ended_on: project.ended_on,
          memo: project.memo,
          details: detailsOf(project.id, money),
          phases: phasesOf(project.id, now),
          ...(money && {
            contract_amount: contracts.reduce(
              (sum, contract) => sum + (amountOn(contract, now) ?? 0),
              0
            ),
          }),
        };
      });

    const statusCounts: Partial<Record<MockProject["status"], number>> = {};
    for (const row of rows) statusCounts[row.status] = (statusCounts[row.status] ?? 0) + 1;
    const sums = new Map<string, number>();
    for (const row of rows)
      for (const detail of row.details)
        if (detail.value_number !== null && detail.value_number !== undefined)
          sums.set(detail.field_id, (sums.get(detail.field_id) ?? 0) + detail.value_number);

    const ledger: Ledger = {
      results: rows,
      totals: {
        project_count: rows.length,
        status_counts: statusCounts,
        supervision_count: rows.filter((row) =>
          row.phases.some((phase) => phase.template_key === "supervision")
        ).length,
        detail_sums: [...sums].map(([field_id, total]) => ({
          field_id,
          total: Math.round(total * 100) / 100,
        })),
        ...(money && {
          contract_amount: rows.reduce((sum, row) => sum + (row.contract_amount ?? 0), 0),
        }),
      },
    };
    return HttpResponse.json(ledger);
  }),

  http.get(`${BASE}/projects/:id/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const project = db.projects.find((row) => row.id === params.id);
    if (!project) return fail(404, "없는 프로젝트입니다.");
    return HttpResponse.json(toDetail(project, canSeeMoney(user.id)));
  }),

  http.post(`${BASE}/projects/`, async ({ request }) => {
    await latency();
    const admin = requireAdmin();
    if (admin instanceof Response) return admin;
    const input = (await request.json()) as ProjectCreateInput;
    if (!input.name?.trim()) return fail(422, "프로젝트명을 적어 주세요.");
    const year = today().slice(2, 4);
    const count = db.projects.filter((project) => project.code?.startsWith(`${year}-`)).length;
    const made: MockProject = {
      id: `p-new-${++seq}`,
      code: `${year}-${String(count + 1).padStart(3, "0")}`,
      name: input.name.trim(),
      color: input.color || "#3182f6",
      status: "planned",
      started_on: null,
      ended_on: null,
      budget_hours: input.budget_hours ?? null,
      memo: input.memo ?? "",
      nas_path: "",
    };
    db.projects.push(made);
    // 기본 단계 행은 서버가 깐다 — 같은 틀에서 온 프로젝트는 같은 단계로 시작한다.
    const template = db.projectPhases.filter((phase) => phase.project_id === "p-2512");
    for (const phase of template)
      db.projectPhases.push({
        ...phase,
        id: `${made.id}-${phase.template_key}`,
        project_id: made.id,
        start_date: null,
        due_date: null,
        done_date: null,
        logs: [],
      });
    return HttpResponse.json(toDetail(made, canSeeMoney(admin.id)), { status: 201 });
  }),

  http.patch(`${BASE}/projects/:id/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const project = db.projects.find((row) => row.id === params.id);
    if (!project) return fail(404, "없는 프로젝트입니다.");
    const patch = (await request.json()) as ProjectPatch;
    if (patch.name !== undefined) project.name = patch.name.trim();
    if (patch.color !== undefined) project.color = patch.color;
    if (patch.status !== undefined) project.status = patch.status;
    if (patch.budget_hours !== undefined) project.budget_hours = patch.budget_hours;
    if (patch.memo !== undefined) project.memo = patch.memo;
    return HttpResponse.json(toDetail(project, canSeeMoney(user.id)));
  }),

  http.put(`${BASE}/projects/:id/phases/:phaseId/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const project = db.projects.find((row) => row.id === params.id);
    const phase = db.projectPhases.find((row) => row.id === params.phaseId);
    if (!project || !phase) return fail(404, "없는 단계입니다.");
    const input = (await request.json()) as PhaseDatesInput;
    phase.start_date = input.start_date ?? null;
    phase.due_date = input.due_date ?? null;
    phase.done_date = input.done_date ?? null;
    return HttpResponse.json(toDetail(project, canSeeMoney(user.id)));
  }),

  http.post(`${BASE}/projects/:id/phases/:phaseId/logs/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const project = db.projects.find((row) => row.id === params.id);
    const phase = db.projectPhases.find((row) => row.id === params.phaseId);
    if (!project || !phase) return fail(404, "없는 단계입니다.");
    const input = (await request.json()) as PhaseLogInput;
    if (!input.text?.trim()) return fail(422, "기록을 적어 주세요.");
    phase.logs.push({
      id: `log-new-${++seq}`,
      log_date: input.log_date,
      // 지난 날짜면 한 일이고, 앞날이면 예정이다.
      status: input.log_date <= today() ? "done" : "planned",
      text: input.text.trim(),
      author_id: user.id,
      created_at: new Date().toISOString(),
    });
    return HttpResponse.json(toDetail(project, canSeeMoney(user.id)));
  }),

  /** PM 을 통째로 — 배치되지 않은 사람은 오늘부터 배치하고 PM 으로 둔다(서버와 같다). */
  http.put(`${BASE}/projects/:id/pms/`, async ({ params, request }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const project = db.projects.find((row) => row.id === params.id);
    if (!project) return fail(404, "없는 프로젝트입니다.");
    const { user_ids: userIds } = (await request.json()) as { user_ids: string[] };
    const active = db.projectMembers.filter(
      (member) => member.project_id === project.id && member.released_on === null
    );
    for (const member of active) member.is_pm = userIds.includes(member.user_id);
    for (const userId of userIds) {
      if (active.some((member) => member.user_id === userId)) continue;
      db.projectMembers.push({
        id: `member-new-${++seq}`,
        project_id: project.id,
        user_id: userId,
        assigned_on: today(),
        released_on: null,
        is_pm: true,
      });
    }
    return HttpResponse.json(toDetail(project, canSeeMoney(user.id)));
  }),

  /** 사무소가 끼우는 단계 — 맨 뒤에 선다. */
  http.post(`${BASE}/projects/:id/phases/`, async ({ params, request }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const project = db.projects.find((row) => row.id === params.id);
    if (!project) return fail(404, "없는 프로젝트입니다.");
    const input = (await request.json()) as PhaseCreateInput;
    if (!input.name?.trim()) return fail(422, "단계 이름을 적어 주세요.");
    const siblings = db.projectPhases.filter((phase) => phase.project_id === project.id);
    db.projectPhases.push({
      id: `phase-new-${++seq}`,
      project_id: project.id,
      name: input.name.trim(),
      template_key: "",
      ordinal: siblings.length + 1,
      sort_order: Math.max(0, ...siblings.map((phase) => phase.sort_order)) + 1,
      start_label: input.start_label?.trim() || "시작일",
      end_label: input.end_label?.trim() || "완료예정일",
      start_date: null,
      due_date: null,
      done_date: null,
      logs: [],
    });
    return HttpResponse.json(toDetail(project, canSeeMoney(user.id)), { status: 201 });
  }),

  /** 보낸 칸만 바꾼다. */
  http.patch(`${BASE}/projects/:id/phases/:phaseId/`, async ({ params, request }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const project = db.projects.find((row) => row.id === params.id);
    const phase = db.projectPhases.find((row) => row.id === params.phaseId);
    if (!project || !phase) return fail(404, "없는 단계입니다.");
    const patch = (await request.json()) as PhaseUpdateInput;
    if (patch.name !== undefined && !patch.name.trim()) return fail(422, "단계 이름을 적어 주세요.");
    Object.assign(
      phase,
      Object.fromEntries(Object.entries(patch).filter(([, value]) => value !== undefined))
    );
    return HttpResponse.json(toDetail(project, canSeeMoney(user.id)));
  }),

  /** 마일스톤이나 재무 기록이 붙은 단계는 지우지 않는다. */
  http.delete(`${BASE}/projects/:id/phases/:phaseId/`, async ({ params }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const phase = db.projectPhases.find((row) => row.id === params.phaseId);
    if (!phase || phase.project_id !== params.id) return fail(404, "없는 단계입니다.");
    if (phase.logs.length || db.billings.some((billing) => billing.phase_id === phase.id))
      return fail(409, "마일스톤이나 재무 기록이 붙은 단계는 지울 수 없습니다.");
    db.projectPhases.splice(db.projectPhases.indexOf(phase), 1);
    return new HttpResponse(null, { status: 204 });
  }),

  /** 단계 순서를 통째로 — 이 프로젝트의 단계가 빠짐없이 한 번씩 있어야 한다. */
  http.put(`${BASE}/projects/:id/phase-order/`, async ({ params, request }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const project = db.projects.find((row) => row.id === params.id);
    if (!project) return fail(404, "없는 프로젝트입니다.");
    const { phase_ids: ids } = (await request.json()) as { phase_ids: string[] };
    const phases = db.projectPhases.filter((phase) => phase.project_id === project.id);
    if (ids.length !== phases.length || phases.some((phase) => !ids.includes(phase.id)))
      return fail(422, "이 프로젝트의 단계가 빠짐없이 한 번씩 있어야 합니다.");
    for (const phase of phases) phase.sort_order = ids.indexOf(phase.id) + 1;
    return HttpResponse.json(toDetail(project, canSeeMoney(user.id)));
  }),

  http.patch(`${BASE}/projects/:id/phases/:phaseId/logs/:logId/`, async ({ params, request }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const project = db.projects.find((row) => row.id === params.id);
    const log = db.projectPhases
      .find((row) => row.id === params.phaseId)
      ?.logs.find((row) => row.id === params.logId);
    if (!project || !log) return fail(404, "없는 마일스톤입니다.");
    const patch = (await request.json()) as Partial<PhaseLogInput>;
    if (patch.text !== undefined && !patch.text.trim()) return fail(422, "기록을 적어 주세요.");
    if (patch.log_date !== undefined) log.log_date = patch.log_date;
    if (patch.text !== undefined) log.text = patch.text.trim();
    return HttpResponse.json(toDetail(project, canSeeMoney(user.id)));
  }),

  http.delete(`${BASE}/projects/:id/phases/:phaseId/logs/:logId/`, async ({ params }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const phase = db.projectPhases.find((row) => row.id === params.phaseId);
    const index = phase?.logs.findIndex((row) => row.id === params.logId) ?? -1;
    if (!phase || index < 0) return fail(404, "없는 마일스톤입니다.");
    phase.logs.splice(index, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  http.post(`${BASE}/projects/:id/members/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const project = db.projects.find((row) => row.id === params.id);
    if (!project) return fail(404, "없는 프로젝트입니다.");
    const input = (await request.json()) as MemberAssignInput;
    // 같은 사람의 배치 기간은 겹칠 수 없다.
    const overlap = db.projectMembers.some(
      (member) =>
        member.project_id === project.id &&
        member.user_id === input.user_id &&
        (member.released_on === null || member.released_on >= input.assigned_on)
    );
    if (overlap) return fail(409, "이미 배치된 사람이에요.");
    db.projectMembers.push({
      id: `member-new-${++seq}`,
      project_id: project.id,
      user_id: input.user_id,
      assigned_on: input.assigned_on,
      released_on: input.released_on ?? null,
      is_pm: false,
    });
    return HttpResponse.json(toDetail(project, canSeeMoney(user.id)), { status: 201 });
  }),

  http.patch(`${BASE}/projects/:id/members/:memberId/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const project = db.projects.find((row) => row.id === params.id);
    const member = db.projectMembers.find((row) => row.id === params.memberId);
    if (!project || !member) return fail(404, "없는 배치입니다.");
    const { released_on } = (await request.json()) as { released_on: string };
    if (released_on < member.assigned_on) return fail(422, "해제일이 배치일보다 앞이에요.");
    member.released_on = released_on;
    return HttpResponse.json(toDetail(project, canSeeMoney(user.id)));
  }),

  /* ── 투입 계획 — 한 기간을 덮어쓰고, 값이 같은 이웃과 합친다 ─────────────── */

  http.put(`${BASE}/projects/:id/members/:memberId/allocation/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const project = db.projects.find((row) => row.id === params.id);
    const member = db.projectMembers.find((row) => row.id === params.memberId);
    if (!project || !member) return fail(404, "없는 배치입니다.");
    const input = (await request.json()) as AllocationInput;
    const problem = allocationProblem(member, input);
    if (problem) return problem;

    const mine = () => db.projectMemberPlans.filter((row) => row.project_member_id === member.id);

    // 1. 덮어쓸 기간에서 기존 계획을 비운다. 양옆으로 삐져나온 부분은 남긴다.
    for (const plan of mine()) {
      if (plan.period_from > input.period_to || plan.period_to < input.period_from) continue;
      const head = plan.period_from < input.period_from;
      const tail = plan.period_to > input.period_to;
      if (head && tail) {
        db.projectMemberPlans.push({
          ...plan,
          id: `plan-split-${++seq}`,
          period_from: shift(input.period_to, 1),
        });
        plan.period_to = shift(input.period_from, -1);
      } else if (head) {
        plan.period_to = shift(input.period_from, -1);
      } else if (tail) {
        plan.period_from = shift(input.period_to, 1);
      } else {
        db.projectMemberPlans.splice(db.projectMemberPlans.indexOf(plan), 1);
      }
    }

    // 2. 0 이면 비우는 것으로 끝. 아니면 새 행을 넣고 값이 같은 이웃과 합친다.
    if (input.daily_hours > 0) {
      const made = {
        id: `plan-new-${++seq}`,
        project_member_id: member.id,
        period_from: input.period_from,
        period_to: input.period_to,
        daily_hours: input.daily_hours.toFixed(2),
        note: input.note ?? "",
      };
      db.projectMemberPlans.push(made);
      for (const side of [-1, 1] as const) {
        const edge = side < 0 ? shift(made.period_from, -1) : shift(made.period_to, 1);
        const near = mine().find(
          (plan) =>
            plan.id !== made.id &&
            plan.daily_hours === made.daily_hours &&
            plan.note === made.note &&
            (side < 0 ? plan.period_to === edge : plan.period_from === edge)
        );
        if (!near) continue;
        if (side < 0) made.period_from = near.period_from;
        else made.period_to = near.period_to;
        db.projectMemberPlans.splice(db.projectMemberPlans.indexOf(near), 1);
      }
    }

    return HttpResponse.json(toDetail(project, canSeeMoney(user.id)));
  }),

  http.put(`${BASE}/projects/:id/details/:fieldId/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const project = db.projects.find((row) => row.id === params.id);
    const field = db.detailFields.find((row) => row.id === params.fieldId);
    if (!project || !field) return fail(404, "없는 항목입니다.");
    if (field.value_type === "money" && !canSeeMoney(user.id))
      return fail(403, "금액 항목을 고칠 권한이 없습니다.");
    const input = (await request.json()) as DetailValueInput;
    const empty =
      (input.value_text ?? null) === null &&
      (input.value_number ?? null) === null &&
      (input.value_date ?? null) === null;
    const index = db.projectDetails.findIndex(
      (row) => row.project_id === project.id && row.field_id === field.id
    );
    if (empty) {
      if (index >= 0) db.projectDetails.splice(index, 1);
    } else {
      const row = {
        project_id: project.id,
        field_id: field.id,
        value_text: input.value_text ?? null,
        value_number: input.value_number ?? null,
        value_date: input.value_date ?? null,
      };
      if (index >= 0) db.projectDetails[index] = row;
      else db.projectDetails.push(row);
    }
    return HttpResponse.json(toDetail(project, canSeeMoney(user.id)));
  }),

  /* ── 금액 ────────────────────────────────────────────────────────── */

  http.get(`${BASE}/projects/:id/money/`, async ({ params }) => {
    await latency();
    const guard = moneyGuard(String(params.id));
    if (guard instanceof Response) return guard;
    return HttpResponse.json(moneyOf(String(params.id)));
  }),

  http.post(`${BASE}/projects/:id/contracts/`, async ({ params, request }) => {
    await latency();
    const guard = moneyGuard(String(params.id));
    if (guard instanceof Response) return guard;
    const input = (await request.json()) as ContractInput;
    if (!input.title?.trim()) return fail(422, "계약명을 적어 주세요.");
    const projectId = String(params.id);
    const count = db.contracts.filter((contract) => contract.project_id === projectId).length;
    db.contracts.push({
      id: `contract-new-${++seq}`,
      project_id: projectId,
      seq: count + 1,
      title: input.title.trim(),
      client_name: input.client_name ?? "",
      signed_on: input.signed_on ?? null,
      contract_from: input.contract_from ?? null,
      contract_to: input.contract_to ?? null,
      vat_included: input.vat_included ?? false,
      memo: input.memo ?? "",
      revisions: [],
      phase_fees: [],
    });
    return HttpResponse.json(moneyOf(projectId), { status: 201 });
  }),

  http.post(
    `${BASE}/projects/:id/contracts/:contractId/revisions/`,
    async ({ params, request }) => {
      await latency();
      const guard = moneyGuard(String(params.id));
      if (guard instanceof Response) return guard;
      const contract = db.contracts.find((row) => row.id === params.contractId);
      if (!contract) return fail(404, "없는 계약입니다.");
      const input = (await request.json()) as RevisionInput;
      if (!input.amount) return fail(422, "금액을 적어 주세요.");
      const from = input.effective_from ?? today();
      // 전 금액은 무효가 아니라 기간이 닫힌다 — 지난 숫자가 그대로 재현되어야 한다.
      for (const revision of contract.revisions)
        if (revision.effective_to === null && revision.voided_at === null)
          revision.effective_to = from;
      contract.revisions.push({
        id: `revision-new-${++seq}`,
        amount: input.amount,
        effective_from: from,
        effective_to: null,
        change_reason: input.change_reason ?? "",
        voided_at: null,
        void_reason: "",
        created_at: new Date().toISOString(),
      });
      return HttpResponse.json(moneyOf(String(params.id)), { status: 201 });
    }
  ),

  http.post(`${BASE}/projects/:id/contracts/:contractId/billings/`, async ({ params, request }) => {
    await latency();
    const guard = moneyGuard(String(params.id));
    if (guard instanceof Response) return guard;
    const contract = db.contracts.find((row) => row.id === params.contractId);
    if (!contract) return fail(404, "없는 계약입니다.");
    const input = (await request.json()) as BillingInput;
    if (!input.amount) return fail(422, "금액을 적어 주세요.");
    const count = db.billings.filter((billing) => billing.contract_id === contract.id).length;
    db.billings.push({
      id: `billing-new-${++seq}`,
      project_id: String(params.id),
      contract_id: contract.id,
      seq: count + 1,
      title: input.title ?? "",
      amount: input.amount,
      phase_id: input.phase_id ?? null,
      planned_on: input.planned_on ?? null,
      billed_on: input.billed_on ?? null,
      due_on: input.due_on ?? null,
      memo: input.memo ?? "",
    });
    return HttpResponse.json(moneyOf(String(params.id)), { status: 201 });
  }),

  http.post(`${BASE}/projects/:id/contracts/:contractId/incomes/`, async ({ params, request }) => {
    await latency();
    const guard = moneyGuard(String(params.id));
    if (guard instanceof Response) return guard;
    const contract = db.contracts.find((row) => row.id === params.contractId);
    if (!contract) return fail(404, "없는 계약입니다.");
    const input = (await request.json()) as Income;
    if (!input.amount) return fail(422, "금액을 적어 주세요.");
    db.incomes.push({
      id: `income-new-${++seq}`,
      project_id: String(params.id),
      contract_id: contract.id,
      billing_id: input.billing_id ?? null,
      amount: input.amount,
      received_on: input.received_on,
      memo: input.memo ?? "",
    });
    return HttpResponse.json(moneyOf(String(params.id)), { status: 201 });
  }),

  /* ── 금액 고치기·지우기 ────────────────────────────────────────────────
   *
   * 지난 숫자가 흔들리면 이미 보고한 수익성이 달라진다. 그래서 **계약 금액은 여기서 고치지
   * 않는다** — 개정(revision)으로 쌓고 무효로 돌린다. 계약 행은 이름·발주처·날짜만 고친다. */

  http.patch(`${BASE}/projects/:id/contracts/:contractId/`, async ({ params, request }) => {
    await latency();
    const guard = moneyGuard(String(params.id));
    if (guard instanceof Response) return guard;
    const contract = db.contracts.find((row) => row.id === params.contractId);
    if (!contract) return fail(404, "없는 계약입니다.");
    const input = (await request.json()) as Partial<ContractInput>;
    if (input.title !== undefined && !input.title.trim()) return fail(422, "계약명을 적어 주세요.");
    Object.assign(contract, {
      title: input.title?.trim() ?? contract.title,
      client_name: input.client_name ?? contract.client_name,
      signed_on: input.signed_on !== undefined ? input.signed_on : contract.signed_on,
      contract_from:
        input.contract_from !== undefined ? input.contract_from : contract.contract_from,
      contract_to: input.contract_to !== undefined ? input.contract_to : contract.contract_to,
      vat_included: input.vat_included ?? contract.vat_included,
      memo: input.memo ?? contract.memo,
    });
    return HttpResponse.json(moneyOf(String(params.id)));
  }),

  http.delete(`${BASE}/projects/:id/contracts/:contractId/`, async ({ params }) => {
    await latency();
    const guard = moneyGuard(String(params.id));
    if (guard instanceof Response) return guard;
    const index = db.contracts.findIndex((row) => row.id === params.contractId);
    if (index < 0) return fail(404, "없는 계약입니다.");
    // 수금이 한 번이라도 들어온 계약은 지우지 않는다 — 돈이 들어온 기록이 사라진다.
    const billingIds = db.billings
      .filter((row) => row.contract_id === params.contractId)
      .map((row) => row.id);
    if (db.incomes.some((row) => row.billing_id && billingIds.includes(row.billing_id)))
      return fail(409, "수금이 있는 계약은 지울 수 없어요.");
    db.contracts.splice(index, 1);
    for (let i = db.billings.length - 1; i >= 0; i--)
      if (db.billings[i].contract_id === params.contractId) db.billings.splice(i, 1);
    return HttpResponse.json(moneyOf(String(params.id)));
  }),

  http.post(
    `${BASE}/projects/:id/contracts/:contractId/revisions/:revisionId/void/`,
    async ({ params, request }) => {
      await latency();
      const guard = moneyGuard(String(params.id));
      if (guard instanceof Response) return guard;
      const contract = db.contracts.find((row) => row.id === params.contractId);
      const revision = contract?.revisions.find((row) => row.id === params.revisionId);
      if (!contract || !revision) return fail(404, "없는 개정입니다.");
      if (revision.voided_at) return fail(409, "이미 무효로 처리된 개정이에요.");
      const { void_reason } = (await request.json().catch(() => ({}))) as { void_reason?: string };
      revision.voided_at = new Date().toISOString();
      revision.void_reason = void_reason ?? "";
      // 무효로 돌리면 그 앞 개정의 기간이 다시 열린다.
      const live = contract.revisions.filter((row) => !row.voided_at);
      const previous = live
        .filter((row) => row.effective_from < revision.effective_from)
        .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0];
      if (previous) previous.effective_to = revision.effective_to;
      return HttpResponse.json(moneyOf(String(params.id)));
    }
  ),

  http.put(
    `${BASE}/projects/:id/contracts/:contractId/phase-fees/:phaseId/`,
    async ({ params, request }) => {
      await latency();
      const guard = moneyGuard(String(params.id));
      if (guard instanceof Response) return guard;
      const contract = db.contracts.find((row) => row.id === params.contractId);
      if (!contract) return fail(404, "없는 계약입니다.");
      const { fee, memo } = (await request.json()) as { fee: number; memo?: string };
      if (fee < 0) return fail(422, "용역비는 0보다 작을 수 없어요.");
      const found = contract.phase_fees.find((row) => row.phase_id === params.phaseId);
      if (found) Object.assign(found, { fee, memo: memo ?? found.memo });
      else contract.phase_fees.push({ phase_id: String(params.phaseId), fee, memo: memo ?? "" });
      return HttpResponse.json(moneyOf(String(params.id)));
    }
  ),

  http.delete(
    `${BASE}/projects/:id/contracts/:contractId/phase-fees/:phaseId/`,
    async ({ params }) => {
      await latency();
      const guard = moneyGuard(String(params.id));
      if (guard instanceof Response) return guard;
      const contract = db.contracts.find((row) => row.id === params.contractId);
      const index = contract?.phase_fees.findIndex((row) => row.phase_id === params.phaseId) ?? -1;
      if (!contract || index < 0) return fail(404, "없는 단계 용역비입니다.");
      contract.phase_fees.splice(index, 1);
      return HttpResponse.json(moneyOf(String(params.id)));
    }
  ),

  http.patch(`${BASE}/projects/:id/billings/:billingId/`, async ({ params, request }) => {
    await latency();
    const guard = moneyGuard(String(params.id));
    if (guard instanceof Response) return guard;
    const billing = db.billings.find((row) => row.id === params.billingId);
    if (!billing) return fail(404, "없는 차수입니다.");
    const input = (await request.json()) as Partial<BillingInput>;
    Object.assign(billing, {
      title: input.title?.trim() ?? billing.title,
      amount: input.amount ?? billing.amount,
      phase_id: input.phase_id !== undefined ? input.phase_id : billing.phase_id,
      planned_on: input.planned_on !== undefined ? input.planned_on : billing.planned_on,
      billed_on: input.billed_on !== undefined ? input.billed_on : billing.billed_on,
      due_on: input.due_on !== undefined ? input.due_on : billing.due_on,
      memo: input.memo ?? billing.memo,
    });
    return HttpResponse.json(moneyOf(String(params.id)));
  }),

  http.delete(`${BASE}/projects/:id/billings/:billingId/`, async ({ params }) => {
    await latency();
    const guard = moneyGuard(String(params.id));
    if (guard instanceof Response) return guard;
    const index = db.billings.findIndex((row) => row.id === params.billingId);
    if (index < 0) return fail(404, "없는 차수입니다.");
    if (db.incomes.some((row) => row.billing_id === params.billingId))
      return fail(409, "수금이 있는 차수는 지울 수 없어요.");
    db.billings.splice(index, 1);
    return HttpResponse.json(moneyOf(String(params.id)));
  }),

  http.delete(`${BASE}/projects/:id/incomes/:incomeId/`, async ({ params }) => {
    await latency();
    const guard = moneyGuard(String(params.id));
    if (guard instanceof Response) return guard;
    const index = db.incomes.findIndex((row) => row.id === params.incomeId);
    if (index < 0) return fail(404, "없는 수금입니다.");
    db.incomes.splice(index, 1);
    return HttpResponse.json(moneyOf(String(params.id)));
  }),

  http.patch(`${BASE}/projects/:id/outsourcings/:outsourcingId/`, async ({ params, request }) => {
    await latency();
    const guard = moneyGuard(String(params.id));
    if (guard instanceof Response) return guard;
    const row = db.outsourcings.find((item) => item.id === params.outsourcingId);
    if (!row) return fail(404, "없는 외주·경비입니다.");
    const input = (await request.json()) as Partial<OutsourcingInput>;
    const kind = input.kind ?? row.kind;
    Object.assign(row, {
      kind,
      kind_label: KIND_LABEL[kind],
      payee_name: input.payee_name?.trim() ?? row.payee_name,
      title: input.title?.trim() ?? row.title,
      amount: input.amount ?? row.amount,
      vat_included: input.vat_included ?? row.vat_included,
      ordered_on: input.ordered_on !== undefined ? input.ordered_on : row.ordered_on,
      planned_on: input.planned_on !== undefined ? input.planned_on : row.planned_on,
      paid_on: input.paid_on !== undefined ? input.paid_on : row.paid_on,
      phase_id: input.phase_id !== undefined ? input.phase_id : row.phase_id,
      memo: input.memo ?? row.memo,
    });
    return HttpResponse.json(moneyOf(String(params.id)));
  }),

  http.delete(`${BASE}/projects/:id/outsourcings/:outsourcingId/`, async ({ params }) => {
    await latency();
    const guard = moneyGuard(String(params.id));
    if (guard instanceof Response) return guard;
    const index = db.outsourcings.findIndex((row) => row.id === params.outsourcingId);
    if (index < 0) return fail(404, "없는 외주·경비입니다.");
    if (db.outsourcings[index].paid_on) return fail(409, "이미 지급한 건은 지울 수 없어요.");
    db.outsourcings.splice(index, 1);
    return HttpResponse.json(moneyOf(String(params.id)));
  }),

  http.post(`${BASE}/projects/:id/outsourcings/`, async ({ params, request }) => {
    await latency();
    const guard = moneyGuard(String(params.id));
    if (guard instanceof Response) return guard;
    const input = (await request.json()) as OutsourcingInput;
    if (!input.payee_name?.trim() || !input.title?.trim())
      return fail(422, "받는 곳과 내용을 적어 주세요.");
    const projectId = String(params.id);
    const count = db.outsourcings.filter((row) => row.project_id === projectId).length;
    const kind = input.kind ?? "outsource";
    db.outsourcings.push({
      id: `outsourcing-new-${++seq}`,
      project_id: projectId,
      seq: count + 1,
      kind,
      kind_label: KIND_LABEL[kind],
      payee_name: input.payee_name.trim(),
      title: input.title.trim(),
      amount: input.amount,
      vat_included: input.vat_included ?? false,
      ordered_on: input.ordered_on ?? null,
      planned_on: input.planned_on ?? null,
      paid_on: input.paid_on ?? null,
      phase_id: input.phase_id ?? null,
      memo: input.memo ?? "",
    });
    return HttpResponse.json(moneyOf(projectId), { status: 201 });
  }),
];
